import React, { useContext, useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Tooltip,
  CircularProgress,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import nodeTypesConfig from "../../models/nodeTypesConfig";
import NodeConfigModal from "./menus/NodeConfigModal";
import NodesDynamicForm from "../../forms/NodesDynamicForm";
import AddNodeContext from "../../contexts/AddNodeContext";
import useAutomations from "../../hooks/automationhooks/useAutomations";
import { useParams } from "react-router-dom";
import { getNodeFormFields } from "../../utils/workflow/getNodeFormFields";
import { getNodeTooltip, sectionTooltips } from "../../utils/workflow/journeyTooltips";
import FieldHelpTooltip from "../common/FieldHelpTooltip";
import { useThemeContext } from "../../Theme/Theme";
import ExternalApiTriggerSideMenu from "./menus/ExternalApiTriggerSideMenu";

const AddNodeButton = ({ nodesExist, nodes = [] }) => {
  const COLOR_SURFACE = "#2C2B2D";
  const COLOR_HEADER = "#1E1E1E";
  const COLOR_ACCENT = "#0DA3E2";
  const { addNode } = useContext(AddNodeContext);
  const {
    brandSegments,
    campaigns,
    handleGetCampaigns,
    handleGetBrandSegments,
  } = useAutomations();
  const { mode } = useThemeContext();
  const { automationId, brandId } = useParams();
  const [optionsModalOpen, setOptionsModalOpen] = useState(false);
  const [configModal, setConfigModal] = useState({
    open: false,
    content: null,
  });

  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const currentBrandId = brandId || localStorage.getItem("brandId");
      if (currentBrandId) {
        setIsLoadingData(true);
        try {
          await Promise.all([
            handleGetCampaigns(currentBrandId),
            handleGetBrandSegments(currentBrandId),
          ]);
        } catch (error) {
          console.error("Error loading initial data:", error);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    loadData();
  }, [brandId]);

  const handleOptionsButtonClick = () => {
    setOptionsModalOpen(true);
  };

  // Close the options modal
  const handleOptionsModalClose = () => {
    setOptionsModalOpen(false);
  };

  // Close the configuration modal
  const handleConfigModalClose = () => {
    setConfigModal({ open: false, content: null });
  };

  const handleOptionSelect = (type) => {
    if (type === "sqlScheduleTrigger" && nodes.length > 0) {
      setOptionsModalOpen(false);
      return;
    }

    setOptionsModalOpen(false);

    if (type === "externalApi") {
      setConfigModal({
        open: true,
        content: (
          <ExternalApiTriggerSideMenu
            onSubmit={(formData) => {
              addNode(type, formData);
            }}
            onClose={handleConfigModalClose}
          />
        ),
      });
      return;
    }

    const formFields = getNodeFormFields(
      type,
      nodeTypesConfig,
      brandSegments,
      campaigns
    );
    const config = findNodeTypeConfig(type);

    if (formFields && formFields.length > 0) {
      setConfigModal({
        open: true,
        content: (
          <NodesDynamicForm
            title={`${config.displayName}`}
            nodeTooltip={getNodeTooltip(type)}
            formFields={formFields}
            onSubmit={(formData) => {
              addNode(type, formData);
              handleConfigModalClose();
            }}
            onClose={() => {
              handleConfigModalClose();
            }}
          />
        ),
      });
    } else {
      addNode(type);
    }
  };

  const findNodeTypeConfig = (type) => {
    const { triggers } = nodeTypesConfig;
    return triggers.find((node) => node.type === type);
  };

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          ...(nodesExist
            ? {
                // Keep clear vertical spacing under the help button
                top: "178px",
                left: "23px",
              }
            : {
                // Center in screen when no nodes
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }),
          zIndex: 10,
        }}
      >
        {nodesExist ? (
          <Button
            onClick={handleOptionsButtonClick}
            variant="contained"
            style={{
              borderRadius: 28,
              minWidth: "auto",
              aspectRatio: "1 / 1",
            }}
            sx={{ backgroundColor: "#0DA3E2" }}
          >
            <AddIcon />
          </Button>
        ) : (
          <Tooltip title={sectionTooltips.addFirstStep} placement="right" arrow>
            <Button
              onClick={handleOptionsButtonClick}
            variant="outlined"
            sx={{
              width: 142,
              height: 138,
              minWidth: 142,
              borderRadius: "7px",
              border: `3px dashed ${COLOR_ACCENT}`,
              p: "5px",
              gap: "10px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textTransform: "none",
              backgroundColor: "transparent",
              "&:hover": {
                border: `3px dashed ${COLOR_ACCENT}`,
                backgroundColor: "rgba(13, 163, 226, 0.08)",
              },
            }}
          >
            <AddIcon sx={{ fontSize: 44, color: COLOR_ACCENT }} />
            <Typography
              sx={{
                color: "#FFFFFF",
                fontSize: "12px",
                lineHeight: 1.2,
                textAlign: "center",
              }}
            >
              Add first step...
            </Typography>
          </Button>
          </Tooltip>
        )}
      </Box>

      {/* Options Modal */}
      <Dialog
        open={optionsModalOpen}
        onClose={handleOptionsModalClose}
        PaperProps={{
          sx: {
            width: "1247px",
            maxWidth: "1247px",
            height: "614px",
            maxHeight: "614px",
            bgcolor: COLOR_SURFACE,
            color: "#FFFFFF",
            borderRadius: "3px",
            border: "1px solid rgba(173, 180, 189, 0.45)",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.45)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "left",
            bgcolor: COLOR_HEADER,
            color: COLOR_ACCENT,
            fontWeight: 700,
            fontSize: "24px",
            lineHeight: 1.2,
            px: "20px",
            py: "14px",
            borderBottom: "1px solid rgba(173, 180, 189, 0.2)",
          }}
        >
          Select Trigger Node
          <Typography
            sx={{
              mt: 0.75,
              color: "rgba(255,255,255,0.75)",
              fontSize: "13px",
              fontWeight: 400,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            בחר את האירוע שמפעיל את הג'ירני
            <FieldHelpTooltip title={sectionTooltips.selectTrigger} iconSize={16} />
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            color: "#FFFFFF",
            bgcolor: COLOR_SURFACE,
            p: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: "0",
            flex: 1,
          }}
        >
          {isLoadingData ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress size={60} />
              <DialogTitle>Loading...</DialogTitle>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                px: "40px",
                py: "22px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "14px",
              }}
            >
              {nodeTypesConfig.triggers.map((node) => (
                <Tooltip
                  key={node.type}
                  title={getNodeTooltip(node.type)}
                  placement="top"
                  arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 380,
                        fontSize: "13px",
                        lineHeight: 1.5,
                        whiteSpace: "pre-line",
                      },
                    },
                  }}
                >
                  <span>
                    <Button
                      variant="outlined"
                      onClick={() => handleOptionSelect(node.type)}
                      sx={{
                    width: 165,
                    height: 165,
                    minWidth: 165,
                    borderRadius: "3px",
                    borderColor: COLOR_ACCENT,
                    backgroundColor: COLOR_SURFACE,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    p: "24px 12px",
                    gap: "1px",
                    textTransform: "none",
                    transition: node.isWip ? "none" : "all 0.2s ease-in-out",
                    cursor: node.isWip ? "not-allowed" : "pointer",
                    opacity: node.isWip ? 0.4 : 1,
                    color: "#FFFFFF",
                    ...(node.isWip
                      ? { pointerEvents: "none" }
                      : {
                          "&:hover": {
                            borderColor: COLOR_ACCENT,
                            backgroundColor: "rgba(13, 163, 226, 0.08)",
                          },
                        }),
                  }}
                >
                  <node.icon sx={{ fontSize: "28px", mb: 0.75, color: COLOR_ACCENT }} />
                  <Typography
                    sx={{
                      color: "#FFFFFF",
                      fontFamily: "Inter, sans-serif",
                      fontSize: "16px",
                      lineHeight: 1.15,
                      fontWeight: 400,
                      letterSpacing: 0,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {node.displayName}
                  </Typography>
                </Button>
                  </span>
                </Tooltip>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: COLOR_SURFACE,
            px: "37px",
            pb: "24px",
            pt: 0,
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleOptionsModalClose}
            sx={{
              width: "143px",
              height: "50px",
              minWidth: "143px",
              borderRadius: "7px",
              borderColor: COLOR_ACCENT,
              color: "#FFFFFF",
              backgroundColor: "transparent",
              px: "10px",
              py: "10px",
              gap: "10px",
              textTransform: "none",
              "&:hover": {
                bgcolor: "rgba(13, 163, 226, 0.08)",
                borderColor: COLOR_ACCENT,
              },
            }}
          >
            Go Back
          </Button>
        </DialogActions>
      </Dialog>

      {/* Configuration Modal */}
      {configModal.open && (
        <NodeConfigModal
          open={configModal.open}
          onClose={handleConfigModalClose}
        >
          {configModal.content}
        </NodeConfigModal>
      )}
    </>
  );
};

export default AddNodeButton;
